import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useSite } from "@/context/SiteContext";
import { getConsent, saveConsent } from "@/lib/consent";

const Row = ({ title, desc, checked, onChange, disabled, testId }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-line p-3.5">
    <div>
      <p className="text-sm font-semibold text-navy">{title}</p>
      <p className="text-xs text-muted2">{desc}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
  </div>
);

export const CookieBanner = () => {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true });

  useEffect(() => {
    if (settings?.require_cookie_consent && !getConsent()) setOpen(true);
  }, [settings]);

  useEffect(() => {
    const handler = () => {
      const c = getConsent();
      if (c) setPrefs({ analytics: c.analytics, marketing: c.marketing });
      setCustom(true);
      setOpen(true);
    };
    window.addEventListener("obt-open-cookie-settings", handler);
    return () => window.removeEventListener("obt-open-cookie-settings", handler);
  }, []);

  const decide = (c) => {
    saveConsent(c);
    setOpen(false);
    setCustom(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-xl rounded-2xl border border-line bg-white p-5 shadow-lift sm:left-6 sm:right-auto sm:p-6"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FEF3C7] text-[#92400E]"><Cookie className="h-5 w-5" /></span>
            <div>
              <h2 className="text-base font-bold">We value your privacy</h2>
              <p className="mt-1 text-sm text-body">
                We use essential cookies to run this site and, with your consent, analytics and marketing cookies (Google Analytics, Meta Pixel). Read our <Link to="/cookie-policy" className="font-semibold text-ocean hover:underline">Cookie Policy</Link>.
              </p>
            </div>
          </div>
          {custom && (
            <div className="mt-4 space-y-2.5" data-testid="cookie-customize-panel">
              <Row title="Essential" desc="Required for the website and checkout to work." checked disabled testId="cookie-toggle-essential" />
              <Row title="Analytics" desc="Helps us understand how visitors use the site." checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testId="cookie-toggle-analytics" />
              <Row title="Marketing" desc="Meta Pixel for measuring our ads." checked={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} testId="cookie-toggle-marketing" />
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => decide({ analytics: true, marketing: true })} className="rounded-full bg-cta px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-cta-hover" data-testid="cookie-accept-btn">Accept All</button>
            <button onClick={() => decide({ analytics: false, marketing: false })} className="rounded-full border border-ocean px-5 py-2.5 text-sm font-semibold text-ocean transition-colors hover:bg-[#F0F7FC]" data-testid="cookie-reject-btn">Reject</button>
            {custom ? (
              <button onClick={() => decide(prefs)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-navy underline-offset-4 hover:underline" data-testid="cookie-save-btn">Save Preferences</button>
            ) : (
              <button onClick={() => setCustom(true)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-navy underline-offset-4 hover:underline" data-testid="cookie-customize-btn">Customize</button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
